/**
 * ObserverMakeCall — lets an observer dial any of their assigned children's
 * parents directly via Tata click-to-call.
 *
 * Flow:
 *   1. On mount, fetch assigned children + recent call history in parallel.
 *   2. Observer picks a child from the dropdown.
 *   3. Click "Call Now" → backend triggers Tata click-to-call.
 *      → Tata rings the observer's registered agent phone first.
 *      → After observer picks up, Tata bridges to the parent.
 *   4. After the call ends, Tata's webhook (once configured) updates the
 *      history row with recording + duration + status. We poll every 8s.
 */
import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Label } from './ui/label';
import { Phone, PhoneCall, RefreshCw, Loader2, CheckCircle2, AlertCircle } from 'lucide-react';
import { useToast } from '../hooks/use-toast';

const API_BASE = `${process.env.REACT_APP_BACKEND_URL}/api/observer`;

const statusColor = (s) => {
  const m = {
    initiated: 'bg-blue-100 text-blue-700',
    triggered: 'bg-blue-100 text-blue-700',
    answered: 'bg-green-100 text-green-700',
    completed: 'bg-green-100 text-green-700',
    missed: 'bg-amber-100 text-amber-700',
    failed: 'bg-red-100 text-red-700',
    unknown: 'bg-slate-100 text-slate-600',
  };
  return m[(s || '').toLowerCase()] || m.unknown;
};

const fmtDate = (iso) => {
  if (!iso) return '—';
  try {
    const d = new Date(iso);
    return d.toLocaleString('en-IN', {
      day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit'
    });
  } catch {
    return iso.slice(0, 19);
  }
};

const ObserverMakeCall = () => {
  const { toast } = useToast();
  const token = localStorage.getItem('observer_token');

  const [children, setChildren] = useState([]);
  const [callMode, setCallMode] = useState('assigned'); // 'assigned' | 'manual'
  const [selectedChildId, setSelectedChildId] = useState('');
  const [manualNumber, setManualNumber] = useState('');
  const [manualName, setManualName] = useState('');
  const [history, setHistory] = useState([]);
  const [loadingChildren, setLoadingChildren] = useState(true);
  const [loadingHistory, setLoadingHistory] = useState(false);
  const [placing, setPlacing] = useState(false);

  const loadChildren = useCallback(async () => {
    try {
      setLoadingChildren(true);
      const res = await axios.get(`${API_BASE}/children`, { params: { token } });
      setChildren(res.data.children || []);
    } catch (e) {
      toast({
        title: 'Could not load children',
        description: e?.response?.data?.detail || 'Try refreshing the page.',
        variant: 'destructive',
      });
    } finally {
      setLoadingChildren(false);
    }
  }, [token, toast]);

  const loadHistory = useCallback(async () => {
    try {
      setLoadingHistory(true);
      const res = await axios.get(`${API_BASE}/call-history`, {
        params: { token, limit: 15 },
      });
      setHistory(res.data.calls || []);
    } catch (e) {
      // History is non-critical — fail silently
      console.warn('history load failed:', e);
    } finally {
      setLoadingHistory(false);
    }
  }, [token]);

  useEffect(() => {
    loadChildren();
    loadHistory();
    // Poll history every 8s so status badges update after a call ends
    const t = setInterval(loadHistory, 8000);
    return () => clearInterval(t);
  }, [loadChildren, loadHistory]);

  const selectedChild = children.find((c) => c.id === selectedChildId);
  const canCall =
    callMode === 'assigned'
      ? !!selectedChildId
      : (manualNumber || '').replace(/\D/g, '').length >= 10;

  const handleCall = async () => {
    // Build params based on mode
    const params = { token };
    let displayTarget = '';
    if (callMode === 'assigned') {
      if (!selectedChildId) {
        toast({ title: 'Pick a child first', variant: 'destructive' });
        return;
      }
      params.child_id = selectedChildId;
      const c = children.find((x) => x.id === selectedChildId);
      displayTarget = c?.name || 'child';
    } else {
      const cleaned = (manualNumber || '').replace(/\D/g, '');
      if (cleaned.length < 10) {
        toast({
          title: 'Enter a valid phone number',
          description: 'Number must be at least 10 digits.',
          variant: 'destructive',
        });
        return;
      }
      params.destination_number = cleaned;
      if (manualName.trim()) params.contact_name = manualName.trim();
      displayTarget = manualName.trim() || cleaned;
    }

    setPlacing(true);
    try {
      const res = await axios.post(`${API_BASE}/call-child`, null, { params });
      toast({
        title: 'Call placed',
        description: res.data.message || `Calling ${displayTarget}…`,
      });
      // Reset manual inputs after successful call
      if (callMode === 'manual') {
        setManualNumber('');
        setManualName('');
      }
      // Refresh history quickly so the new row shows up
      setTimeout(loadHistory, 1500);
    } catch (e) {
      toast({
        title: 'Could not place call',
        description: e?.response?.data?.detail || 'Tata calling service error.',
        variant: 'destructive',
      });
    } finally {
      setPlacing(false);
    }
  };

  return (
    <div className="space-y-6">
      {/* Dial card */}
      <Card data-testid="observer-make-call-card">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <PhoneCall className="w-5 h-5 text-violet-600" />
            Make a Call
          </CardTitle>
          <p className="text-sm text-slate-500 mt-1">
            Your phone will ring first — pick up and you'll be connected to the parent.
          </p>
        </CardHeader>
        <CardContent>
          {/* Mode toggle */}
          <div className="flex gap-2 mb-4" data-testid="observer-call-mode-toggle">
            <button
              type="button"
              onClick={() => setCallMode('assigned')}
              className={`px-3 py-1.5 rounded-full text-xs font-medium transition ${
                callMode === 'assigned'
                  ? 'bg-violet-600 text-white'
                  : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
              }`}
              data-testid="observer-call-mode-assigned"
            >
              From assigned children
            </button>
            <button
              type="button"
              onClick={() => setCallMode('manual')}
              className={`px-3 py-1.5 rounded-full text-xs font-medium transition ${
                callMode === 'manual'
                  ? 'bg-violet-600 text-white'
                  : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
              }`}
              data-testid="observer-call-mode-manual"
            >
              New number
            </button>
          </div>

          {callMode === 'assigned' ? (
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 items-end">
              <div className="md:col-span-2">
                <Label className="text-xs sm:text-sm">Select Child</Label>
                {loadingChildren ? (
                  <div className="h-11 flex items-center text-slate-500 text-sm">
                    <Loader2 className="w-4 h-4 animate-spin mr-2" /> Loading your children…
                  </div>
                ) : children.length === 0 ? (
                  <div className="h-11 flex items-center text-slate-500 text-sm">
                    No children assigned yet.
                  </div>
                ) : (
                  <select
                    value={selectedChildId}
                    onChange={(e) => setSelectedChildId(e.target.value)}
                    className="flex h-11 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
                    data-testid="observer-call-child-select"
                  >
                    <option value="">— choose a child —</option>
                    {children.map((c) => (
                      <option key={c.id} value={c.id}>
                        {c.name}
                        {c.parent_phone ? `  ·  ${c.parent_phone}` : ''}
                      </option>
                    ))}
                  </select>
                )}
                {selectedChild && (
                  <p className="text-xs text-slate-500 mt-2">
                    Will dial parent at{' '}
                    <span className="font-mono">
                      {selectedChild.parent_phone || '(no phone on record)'}
                    </span>
                  </p>
                )}
              </div>
              <div>
                <Button
                  onClick={handleCall}
                  disabled={placing || !canCall}
                  className="w-full bg-violet-600 hover:bg-violet-700 h-11"
                  data-testid="observer-call-now-btn"
                >
                  {placing ? (
                    <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Placing call…</>
                  ) : (
                    <><Phone className="w-4 h-4 mr-2" /> Call Now</>
                  )}
                </Button>
              </div>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-[1fr_1fr_auto] gap-4 items-end">
              <div>
                <Label className="text-xs sm:text-sm">Phone Number</Label>
                <input
                  type="tel"
                  inputMode="numeric"
                  value={manualNumber}
                  onChange={(e) => setManualNumber(e.target.value)}
                  placeholder="e.g. 9876543210"
                  className="flex h-11 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
                  data-testid="observer-call-manual-number-input"
                />
                <p className="text-xs text-slate-500 mt-1 leading-tight">
                  10-digit Indian number (country code added automatically)
                </p>
              </div>
              <div>
                <Label className="text-xs sm:text-sm">
                  Contact Name <span className="text-slate-400">(optional)</span>
                </Label>
                <input
                  type="text"
                  value={manualName}
                  onChange={(e) => setManualName(e.target.value)}
                  placeholder="e.g. Parent of Rahul"
                  className="flex h-11 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
                  data-testid="observer-call-manual-name-input"
                />
                <p className="text-xs text-slate-500 mt-1 leading-tight">&nbsp;</p>
              </div>
              <div className="md:w-44">
                <Button
                  onClick={handleCall}
                  disabled={placing || !canCall}
                  className="w-full bg-violet-600 hover:bg-violet-700 h-11"
                  data-testid="observer-call-now-btn"
                >
                  {placing ? (
                    <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Placing call…</>
                  ) : (
                    <><Phone className="w-4 h-4 mr-2" /> Call Now</>
                  )}
                </Button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Recent calls list */}
      <Card data-testid="observer-call-history-card">
        <CardHeader className="flex flex-row items-center justify-between">
          <CardTitle className="text-base">Recent Calls</CardTitle>
          <Button
            variant="outline"
            size="sm"
            onClick={loadHistory}
            disabled={loadingHistory}
            data-testid="observer-call-history-refresh"
          >
            <RefreshCw className={`w-3.5 h-3.5 mr-1.5 ${loadingHistory ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
        </CardHeader>
        <CardContent>
          {history.length === 0 ? (
            <div className="text-center py-8 text-slate-500 text-sm">
              No calls yet. Place your first call above ☝️
            </div>
          ) : (
            <div className="divide-y">
              {history.map((c, idx) => (
                <div
                  key={c.call_id || idx}
                  className="py-3 flex flex-wrap items-center justify-between gap-2"
                  data-testid="observer-call-history-row"
                >
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2">
                      <span className="font-medium text-slate-800 truncate">
                        {c.child_name || 'Unknown child'}
                      </span>
                      {c.triggered_by === 'observer_manual' && (
                        <span className="text-[10px] uppercase tracking-wide bg-violet-100 text-violet-700 px-1.5 py-0.5 rounded">
                          Manual
                        </span>
                      )}
                      {c.triggered_by === 'scheduled' && c.source === 'scheduled' && (
                        <span className="text-[10px] uppercase tracking-wide bg-slate-100 text-slate-600 px-1.5 py-0.5 rounded">
                          Auto
                        </span>
                      )}
                    </div>
                    <div className="text-xs text-slate-500 mt-0.5 flex items-center gap-3 flex-wrap">
                      <span>{fmtDate(c.created_at)}</span>
                      {c.parent_phone_masked && <span className="font-mono">{c.parent_phone_masked}</span>}
                      {c.duration_seconds ? <span>{Math.round(c.duration_seconds)}s</span> : null}
                    </div>
                    {c.diagnostic && (
                      <div className="text-xs text-amber-700 mt-1 flex items-start gap-1 leading-snug" data-testid="observer-call-diagnostic">
                        <AlertCircle className="w-3.5 h-3.5 mt-0.5 shrink-0" />
                        <span>{c.diagnostic}</span>
                      </div>
                    )}
                  </div>
                  <div className="flex items-center gap-2">
                    <span className={`text-xs px-2 py-1 rounded-full font-medium ${statusColor(c.status)}`}>
                      {c.status}
                    </span>
                    {c.recording_url ? (
                      <span className="inline-flex items-center gap-1 text-xs text-green-700">
                        <CheckCircle2 className="w-3.5 h-3.5" /> Recording
                      </span>
                    ) : (
                      <span className="inline-flex items-center gap-1 text-xs text-slate-400">
                        <AlertCircle className="w-3.5 h-3.5" /> No recording
                      </span>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default ObserverMakeCall;
